import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { joinByCode, previewJoinCode } from "@/api/clans";
import { Alert, Button, Spinner } from "@/components/ui";
import { ClassIcon } from "./class-theme";

/** Landing for a class join link / QR code (`/app/community/join/:code`).
 *  Shows a preview of the class, then joins instantly on confirm. */
export function JoinClassPage() {
  const { t } = useTranslation();
  const { code = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const previewQuery = useQuery({
    queryKey: ["join-preview", code],
    queryFn: () => previewJoinCode(code),
    enabled: !!code,
    retry: false,
  });

  const joinMut = useMutation({
    mutationFn: () => joinByCode(code),
    onSuccess: (clan) => {
      qc.invalidateQueries({ queryKey: ["clans"] });
      navigate(`/app/community/clans/${clan.id}`);
    },
  });

  const preview = previewQuery.data;

  return (
    <div className="mx-auto max-w-md animate-fade-up">
      <Link to="/app/community" className="text-sm text-brand-600 hover:underline">
        ← {t("Community")}
      </Link>

      {previewQuery.isLoading && (
        <div className="flex justify-center py-20 text-brand-600">
          <Spinner className="h-7 w-7" />
        </div>
      )}

      {previewQuery.isError && (
        <div className="mt-8">
          <Alert>{t("That join code is invalid or has expired.")}</Alert>
        </div>
      )}

      {preview && (
        <div className="card mt-8 flex flex-col items-center gap-4 p-8 text-center">
          <ClassIcon emoji={preview.icon_emoji} color={preview.color} size={72} />
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{preview.name}</h1>
            <p className="mt-1 text-ink-faint">
              {t("{{count}} members", { count: preview.member_count })}
            </p>
            {preview.description && (
              <p className="mt-2 text-sm text-ink-faint">{preview.description}</p>
            )}
          </div>

          {joinMut.isError && (
            <Alert>
              {joinMut.error instanceof Error ? joinMut.error.message : t("Couldn't join.")}
            </Alert>
          )}

          {preview.already_member ? (
            <Button onClick={() => navigate(`/app/community/clans/${preview.id}`)}>
              {t("Open class")}
            </Button>
          ) : (
            <Button loading={joinMut.isPending} onClick={() => joinMut.mutate()}>
              {t("Join class")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
